import React, { useState, useRef, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table'
import { MoreHorizontal, Eye, Copy, Pencil, Link2, Archive, Trash2, AlertCircle, MessageSquare, CheckSquare, Paperclip, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import TagComponent from '../../../../components/ui/Tag'
import { ColumnSort } from '../PipelineContext'
import { getNextColumnSort } from '../pipelineSort'
import { formatPipelineDeadline, getPipelineDeadlineTime, isPipelineDeadlineOverdue } from '../pipelineDateUtils'

const SORTABLE_COLUMN_IDS = new Set(['title', 'step', 'tags', 'deadline', 'details'])

const TableContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: white;
  border-radius: 0.5rem;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHead = styled.thead`
  background-color: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
`

const TableHeaderCell = styled.th<{ $sortable?: boolean }>`
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    background-color: ${({ $sortable }) => ($sortable ? '#F3F4F6' : 'inherit')};
  }
`

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const TableBody = styled.tbody``

const TableRow = styled.tr<{ $urgency?: boolean }>`
  border-bottom: 1px solid #E5E7EB;
  transition: background-color 0.15s;
  background-color: ${props => props.$urgency ? '#FEF2F2' : 'transparent'};

  &:hover {
    background-color: ${props => props.$urgency ? '#FECACA' : '#F9FAFB'};
  }
`

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #111827;
  vertical-align: middle;
`

const UrgencyCell = styled.td`
  padding: 1rem 0.5rem;
  width: 30px;
  text-align: center;
  vertical-align: middle;
`

const UrgencyIcon = styled.div`
  color: #DC2626;
  display: flex;
  align-items: center;
  justify-content: center;
`

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`

const DetailsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #6B7280;
  font-size: 0.75rem;
`

const DetailItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const DeadlineText = styled.span<{ $overdue: boolean }>`
  color: ${props => props.$overdue ? '#DC2626' : '#111827'};
  font-weight: ${props => props.$overdue ? 700 : 400};
`

const AddCardInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  outline: none;
  
  &:focus {
    border-color: #0090D3;
  }
`

const StepSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  background: white;
  outline: none;
  cursor: pointer;
  min-width: 150px;
  
  &:focus {
    border-color: #0090D3;
  }
`

const AddCardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`

const AddCardButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &.primary {
    background-color: #0090D3;
    color: white;
    
    &:hover {
      background-color: #007BB8;
    }
    
    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background-color: #f5f5f5;
    color: #666;
    
    &:hover {
      background-color: #e0e0e0;
    }
  }
`

const AddCardRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;
  background-color: #F0F9FF;
`

const ActionsCell = styled.div`
  position: relative;
  display: inline-block;
`

const ActionButton = styled.button`
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #6B7280;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    color: #111827;
  }
`

interface ActionMenuProps {
  top?: number
  left?: number
  direction?: 'up' | 'down'
}

const ActionMenu = styled.div<ActionMenuProps>`
  position: fixed;
  top: ${({ top }) => (typeof top === 'number' ? `${top}px` : 'unset')};
  left: ${({ left }) => (typeof left === 'number' ? `${left}px` : 'unset')};
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 2147483647;
  min-width: 160px;
  padding: 8px 0;
  animation: ${({ direction }) => direction === 'up' ? 'slideUp' : 'slideDown'} 0.18s cubic-bezier(.4,0,.2,1);

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`

const ActionMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.$danger ? '#DC2626' : '#303030'};
  text-align: left;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.$danger ? '#FEF2F2' : '#f5f5f5'};
  }

  svg {
    color: ${props => props.$danger ? '#DC2626' : '#666666'};
    flex-shrink: 0;
  }
`

interface Step {
  id: number
  name: string
  color?: string
  allowCardCreation?: boolean
}

interface Tag {
  tag: {
    id: number
    name: string
    color?: string
  }
}

interface Ticket {
  id: number
  title: string
  stepId: number
  urgency?: boolean
  deadline?: string
  comments: any[]
  tags: Tag[]
  tasks?: { id: number; completed: boolean }[]
  fileInTickets?: { id: number }[]
}

interface TableViewProps {
  tickets: Ticket[]
  steps: Step[]
  columnSort: ColumnSort | null
  onColumnSortChange: (sort: ColumnSort) => void
  onView: (ticketId: number) => void
  onDuplicate: (ticketId: number) => void
  onRename: (ticketId: number) => void
  onCopyLink: (ticketId: number) => void
  onArchive: (ticketId: number) => void
  onDelete: (ticketId: number) => void
  showArchived?: boolean
  isAddingCard?: boolean
  onCancelAddCard?: () => void
  onCreate?: (title: string, stepId: number) => void
  editingTicketId?: number | null
  titleDraft?: string
  onTitleDraftChange?: (value: string) => void
  onSaveTitle?: (ticketId: number) => void
  onCancelEdit?: () => void
}

const TableView: React.FC<TableViewProps> = ({
  tickets,
  steps,
  columnSort,
  onColumnSortChange,
  onView,
  onDuplicate,
  onRename,
  onCopyLink,
  onArchive,
  onDelete,
  showArchived = false,
  isAddingCard = false,
  onCancelAddCard,
  onCreate,
  editingTicketId,
  titleDraft,
  onTitleDraftChange,
  onSaveTitle,
  onCancelEdit,
}) => {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; direction: 'up' | 'down' }>({ top: 0, left: 0, direction: 'up' })
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  const [newCardTitle, setNewCardTitle] = useState('')
  const [newCardStepId, setNewCardStepId] = useState<number | null>(null)

  const getStepName = (stepId: number) => {
    const step = steps.find(s => s.id === stepId)
    return step?.name || '-'
  }

  const handleToggleMenu = (ticketId: number) => {
    if (openMenuId === ticketId) {
      setOpenMenuId(null)
      return
    }

    const button = buttonRefs.current[ticketId]
    if (button) {
      const buttonRect = button.getBoundingClientRect()
      const menuHeight = 250
      const menuWidth = 160

      const buttonCenterX = buttonRect.left + (buttonRect.width / 2)
      let left = buttonCenterX - (menuWidth / 2)
      
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10
      }
      if (left < 10) {
        left = 10
      }

      let direction: 'up' | 'down' = 'up'
      let top = buttonRect.top - menuHeight - 5

      if (top < 10) {
        direction = 'down'
        top = buttonRect.bottom + 5
      }

      setMenuPosition({ top, left, direction })
    }
    
    setOpenMenuId(ticketId)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        const isButtonClick = Object.values(buttonRefs.current).some(btn => btn && btn.contains(e.target as Node))
        if (!isButtonClick) {
          setOpenMenuId(null)
        }
      }
    }
    
    const handleScrollOrResize = () => {
      setOpenMenuId(null)
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  useEffect(() => {
    if (isAddingCard && steps.length > 0 && newCardStepId === null) {
      const availableStep = steps.find(s => s.allowCardCreation !== false)
      if (availableStep) {
        setNewCardStepId(availableStep.id)
      }
    }
    if (!isAddingCard) {
      setNewCardTitle('')
      setNewCardStepId(null)
    }
  }, [isAddingCard, steps, newCardStepId])

  const handleCreateCard = () => {
    if (newCardTitle.trim() && newCardStepId && onCreate) {
      onCreate(newCardTitle.trim(), newCardStepId)
      setNewCardTitle('')
      const availableStep = steps.find(s => s.allowCardCreation !== false)
      setNewCardStepId(availableStep ? availableStep.id : null)
      onCancelAddCard?.()
    }
  }

  const handleCancelCreate = () => {
    setNewCardTitle('')
    const availableStep = steps.find(s => s.allowCardCreation !== false)
    setNewCardStepId(availableStep ? availableStep.id : null)
    onCancelAddCard?.()
  }

  const getFirstTagName = (ticket: Ticket) => {
    const names = [...(ticket.tags ?? [])]
      .map((tag) => tag.tag.name)
      .sort((a, b) => a.localeCompare(b))
    return names[0] ?? ''
  }

  const getDetailsCount = (ticket: Ticket) =>
    (ticket.comments?.length ?? 0) +
    (ticket.tasks?.length ?? 0) +
    (ticket.fileInTickets?.length ?? 0)

  const sorting = useMemo(
    () => (columnSort ? [{ id: columnSort.id, desc: columnSort.desc }] : []),
    [columnSort],
  )

  const columns = useMemo<ColumnDef<Ticket>[]>(() => [
    {
      id: 'urgency',
      header: '',
      enableSorting: false,
      size: 30,
      cell: ({ row }) => (
        row.original.urgency ? (
          <UrgencyIcon>
            <AlertCircle size={16} />
          </UrgencyIcon>
        ) : null
      ),
    },
    {
      id: 'title',
      accessorKey: 'title',
      header: 'Nome do card',
      enableSorting: true,
      size: 250,
      cell: ({ row }) => (
        editingTicketId === row.original.id ? (
          <AddCardInput
            value={titleDraft || ''}
            onChange={(e) => onTitleDraftChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSaveTitle?.(row.original.id)
              if (e.key === 'Escape') onCancelEdit?.()
            }}
            onBlur={() => onSaveTitle?.(row.original.id)}
            autoFocus
          />
        ) : (
          row.original.title
        )
      ),
    },
    {
      id: 'step',
      accessorFn: (row) => getStepName(row.stepId),
      header: 'Etapa',
      enableSorting: true,
      cell: ({ row }) => getStepName(row.original.stepId),
    },
    {
      id: 'tags',
      accessorFn: (row) => getFirstTagName(row),
      header: 'Tags',
      enableSorting: true,
      cell: ({ row }) => (
        <TagsContainer>
          {row.original.tags?.map(tag => (
            <TagComponent key={tag.tag.id} name={tag.tag.name} color={tag.tag.color} />
          ))}
        </TagsContainer>
      ),
    },
    {
      id: 'deadline',
      accessorFn: (row) => getPipelineDeadlineTime(row.deadline),
      header: 'Data de vencimento',
      enableSorting: true,
      cell: ({ row }) => (
        row.original.deadline ? (
          <DeadlineText $overdue={isPipelineDeadlineOverdue(row.original.deadline)}>
            {formatPipelineDeadline(row.original.deadline)}
          </DeadlineText>
        ) : '-'
      ),
    },
    {
      id: 'details',
      accessorFn: (row) => getDetailsCount(row),
      header: 'Detalhes',
      enableSorting: true,
      cell: ({ row }) => (
        <DetailsCell>
          {row.original.comments && row.original.comments.length > 0 && (
            <DetailItem>
              <MessageSquare size={14} /> {row.original.comments.length}
            </DetailItem>
          )}
          {row.original.tasks && row.original.tasks.length > 0 && (
            <DetailItem>
              <CheckSquare size={14} /> {row.original.tasks.length}
            </DetailItem>
          )}
          {row.original.fileInTickets && row.original.fileInTickets.length > 0 && (
            <DetailItem>
              <Paperclip size={14} /> {row.original.fileInTickets.length}
            </DetailItem>
          )}
        </DetailsCell>
      ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      size: 50,
      cell: ({ row }) => (
        <ActionsCell>
          <ActionButton 
            ref={el => { buttonRefs.current[row.original.id] = el }}
            onClick={() => handleToggleMenu(row.original.id)}
          >
            <MoreHorizontal size={18} />
          </ActionButton>
        </ActionsCell>
      ),
    },
  ], [
    editingTicketId,
    titleDraft,
    onTitleDraftChange,
    onSaveTitle,
    onCancelEdit,
    steps,
  ])

  const table = useReactTable({
    data: tickets,
    columns,
    state: { sorting },
    manualSorting: true,
    onSortingChange: () => {},
    getCoreRowModel: getCoreRowModel(),
  })

  const renderSortIcon = (columnId: string) => {
    const entry = sorting.find((item) => item.id === columnId)
    if (!entry) {
      return <ArrowUpDown size={14} style={{ opacity: 0.4 }} />
    }
    return entry.desc ? <ArrowDown size={14} /> : <ArrowUp size={14} />
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isSortable = SORTABLE_COLUMN_IDS.has(header.column.id)
                return (
                <TableHeaderCell
                  key={header.id}
                  $sortable={isSortable}
                  style={{ width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : undefined }}
                  onClick={() => {
                    if (isSortable) {
                      onColumnSortChange(getNextColumnSort(header.column.id, columnSort))
                    }
                  }}
                >
                  <HeaderContent>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {isSortable && renderSortIcon(header.column.id)}
                  </HeaderContent>
                </TableHeaderCell>
              )})}
            </tr>
          ))}
        </TableHead>
        <TableBody>
          {isAddingCard && (
            <AddCardRow>
              <UrgencyCell />
              <TableCell>
                <AddCardInput
                  placeholder="Nome do card"
                  value={newCardTitle}
                  onChange={(e) => setNewCardTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateCard()
                    if (e.key === 'Escape') handleCancelCreate()
                  }}
                  autoFocus
                />
              </TableCell>
              <TableCell>
                <StepSelect
                  value={newCardStepId || ''}
                  onChange={(e) => setNewCardStepId(Number(e.target.value))}
                >
                  {steps.filter(step => step.allowCardCreation !== false).map(step => (
                    <option key={step.id} value={step.id}>{step.name}</option>
                  ))}
                </StepSelect>
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell>
                <AddCardActions>
                  <AddCardButton 
                    className="primary" 
                    onClick={handleCreateCard}
                    disabled={!newCardTitle.trim()}
                  >
                    Criar
                  </AddCardButton>
                  <AddCardButton className="secondary" onClick={handleCancelCreate}>
                    Cancelar
                  </AddCardButton>
                </AddCardActions>
              </TableCell>
            </AddCardRow>
          )}
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id} $urgency={row.original.urgency}>
              {row.getVisibleCells().map(cell => {
                if (cell.column.id === 'urgency') {
                  return (
                    <UrgencyCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </UrgencyCell>
                  )
                }
                return (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {openMenuId !== null && ReactDOM.createPortal(
        <ActionMenu
          ref={menuRef}
          top={menuPosition.top}
          left={menuPosition.left}
          direction={menuPosition.direction}
        >
          <ActionMenuItem onClick={() => { onView(openMenuId); setOpenMenuId(null) }}>
            <Eye size={16} /> Visualizar
          </ActionMenuItem>
          <ActionMenuItem onClick={() => { onDuplicate(openMenuId); setOpenMenuId(null) }}>
            <Copy size={16} /> Duplicar
          </ActionMenuItem>
          <ActionMenuItem onClick={() => { onRename(openMenuId); setOpenMenuId(null) }}>
            <Pencil size={16} /> Renomear
          </ActionMenuItem>
          <ActionMenuItem onClick={() => { onCopyLink(openMenuId); setOpenMenuId(null) }}>
            <Link2 size={16} /> Copiar link
          </ActionMenuItem>
          <ActionMenuItem onClick={() => { onArchive(openMenuId); setOpenMenuId(null) }}>
            <Archive size={16} /> {showArchived ? 'Desarquivar' : 'Arquivar'}
          </ActionMenuItem>
          <ActionMenuItem $danger onClick={() => { onDelete(openMenuId); setOpenMenuId(null) }}>
            <Trash2 size={16} /> Excluir
          </ActionMenuItem>
        </ActionMenu>,
        document.body
      )}
    </TableContainer>
  )
}

export default TableView
